import {useContext, useEffect, useState} from 'react'
import _ from 'lodash'
import {AuthContext, RequirePermission} from '@/features/auth'
import {goodBadLabelStyle} from '@/utils/colors'
import {ILabelingWidgetConfig, ILabelingWidgetProps} from '..'
import {AddWords} from './components/add-word'
import {IWordProps, Word} from './components/word'
import './style.css'

export interface ICategoricalsLabel {
  [key: string]: {
    [word: string]: number
  }
}

export interface ICategoricalLabelingWidgetProps extends ILabelingWidgetProps<ICategoricalsLabel> {
  computeWordStyle?: (key: string, word: string) => React.CSSProperties | undefined
  wordProps?: (key: string, word: string) => Partial<IWordProps>
}

function computeLabelStyle(data: ICategoricalsLabel) {
  const scores = _.chain(data)
    .values()
    .map((v) => _.values(v))
    .flatten()
    .value()
  if (scores.length === 0) return
  return goodBadLabelStyle(_.sum(scores) / scores.length / 5)
}

function LabelingWidget(props: ICategoricalLabelingWidgetProps) {
  const {
    predictionStats,
    selectedEvent,
    currentLabelData,
    disabled,
    // implicitPredictionData,
    implicitLabelData,
    wordProps,
    setCurrentLabelData,
    deleteLabelData,
    computeWordStyle,
  } = props

  const {havePermission} = useContext(AuthContext)

  const [categoryWords, setCategoryWords] = useState<{
    [key: string]: string[]
  }>()
  // const [missingCategoryWords, setMissingCategoryWords] = useState<{
  //     [key:string]: string[],
  // }>();
  const [missingPredictions, setMissingPredictions] = useState<{
    [key: string]: {
      [word: string]: number
    }
  }>()

  function getWords(value: unknown, key?: string): string[] {
    const v = (key ? _.get(value, key) : value) as unknown
    if (!v) return []
    if (_.isPlainObject(v)) return _.keys(v)
    if (_.isArray(v)) return v as string[]
    return []
  }

  useEffect(() => {
    if (!predictionStats) return
    const categories = _.chain(predictionStats.categories)
      .toPairs()
      .map(([k, v]) => {
        return [k, getWords(v)]
      })
      .fromPairs()
      .value()
    setCategoryWords(categories)
  }, [predictionStats])

  useEffect(() => {
    if (!predictionStats) return
    // const categoryDiff = (
    //     _.chain(predictionStats.categories)
    //     .toPairs()
    //     .map(([k, v]) => {
    //         return [k, _.difference(
    //             getWords(v),
    //             getWords(selectedEvent, k),
    //             getWords(currentLabelData, k),
    //         )];
    //     })
    //     .fromPairs()
    //     .value()
    // );
    // setMissingCategoryWords(categoryDiff);

    const predictionDiff = _.chain(currentLabelData)
      .toPairs()
      .map(([k, v]) => {
        const diff = _.difference(getWords(v), getWords(selectedEvent, k))
        if (diff.length === 0) return
        return [k, _.pick(v, diff)]
      })
      .compact()
      .fromPairs()
      .value()
    setMissingPredictions(predictionDiff)
  }, [predictionStats, selectedEvent, currentLabelData])

  function handleSetCategoricalLabel(key: string, word: string, value: number) {
    setCurrentLabelData({
      ...currentLabelData,
      [key]: {
        ..._.get(currentLabelData, key),
        [word]: value,
      },
    })
  }

  function handleDeleteCategoricalLabel(key: string, word: string) {
    const res: ICategoricalsLabel = {
      ...currentLabelData,
      [key]: {
        ..._.omit(_.get(currentLabelData, key), [word]),
      },
    }
    if (_.keys(_.get(res, key)).length === 0) delete res[key]
    if (_.keys(res).length === 0) deleteLabelData()
    else setCurrentLabelData(res)
  }

  if (!predictionStats) return null

  return (
    <div className='labeling-widget categoricals'>
      {_.chain([
        _.keys(predictionStats?.categories),
        _.keys(currentLabelData),
        _.keys(implicitLabelData),
      ])
        .flatten()
        .sort()
        .sortedUniq()
        .map((k) => {
          let itemValue = _.get(selectedEvent, k)
          if (_.isPlainObject(itemValue)) {
            itemValue = _.chain(itemValue)
              .toPairs()
              .sortBy(([_word, score]) => [score])
              .reverse()
              .value()
          } else itemValue = _.map(itemValue, (k) => [k, undefined])
          const words = _.get(categoryWords, k) || []
          // const missingWords = _.get(missingCategoryWords, k) || [];
          const currentLabelWords = _.keys(_.get(currentLabelData, k))
          const implicitLabelWords = _.chain(implicitLabelData)
            .get(k)
            .keys()
            .filter((word) => {
              const scorePath = `["${k}"]["${word}"]`
              const scoreValue = _.get(currentLabelData, scorePath) as number | undefined
              return scoreValue === undefined
            })
            .value()
          return (
            <div
              key={k}
              className='categorical-group'
            >
              <div className='label'>
                <div>{_.chain(k).split('_').join(' ').value()}</div>
                <RequirePermission require={'label.add_label'}>
                  <AddWords
                    currentValue={currentLabelWords}
                    options={words}
                    onAdd={(values) => _.forEach(values, (v) => handleSetCategoricalLabel(k, v, 5))}
                    disabled={disabled}
                  />
                </RequirePermission>
              </div>

              {itemValue ?
                <div className='content'>
                  {_.chain(itemValue)
                    .map(([word, score]) => {
                      const scorePath = `["${k}"]["${word}"]`
                      const scoreValue = _.get(currentLabelData, scorePath) as number | undefined
                      return (
                        <Word
                          readOnly={disabled || !havePermission('label.add_label')}
                          key={word}
                          word={word}
                          score={score}
                          labelScore={scoreValue}
                          onSet={(word, value) => handleSetCategoricalLabel(k, word, value)}
                          onDeleteWord={(word) => handleDeleteCategoricalLabel(k, word)}
                          computeWordStyle={
                            computeWordStyle ? (word) => computeWordStyle(k, word) : undefined
                          }
                          {...(wordProps ? wordProps(k, word) : undefined)}
                        />
                      )
                    })
                    .value()}
                </div>
              : null}

              {_.get(missingPredictions, k) ?
                <div className='content missing'>
                  <div className='label'>missing prediction:</div>
                  {_.chain(missingPredictions)
                    .get(k)
                    .toPairs()
                    .map(([word, score]) => {
                      return (
                        <Word
                          readOnly={disabled || !havePermission('label.add_label')}
                          // canDelete={false}
                          key={word}
                          word={word}
                          labelScore={score}
                          onSet={(word, score) => handleSetCategoricalLabel(k, word, score)}
                          onDeleteWord={(word) => handleDeleteCategoricalLabel(k, word)}
                          computeWordStyle={
                            computeWordStyle ? (word) => computeWordStyle(k, word) : undefined
                          }
                          {...(wordProps ? wordProps(k, word) : undefined)}
                        />
                      )
                    })
                    .value()}
                </div>
              : null}

              {implicitLabelWords.length > 0 ?
                <div className='content implicit'>
                  <div className='label'>implicit label:</div>
                  {_.chain(implicitLabelWords)
                    .map((word) => {
                      const scorePath = `["${k}"]["${word}"]`
                      const implicitScore = _.get(implicitLabelData, scorePath) as
                        | number
                        | undefined
                      if (implicitScore === undefined) return
                      return [word, implicitScore] as [string, number]
                    })
                    .compact()
                    .map(([word, score]) => {
                      return (
                        <Word
                          readOnly={disabled || !havePermission('label.add_label')}
                          canDelete={false}
                          key={word}
                          word={word}
                          labelScore={score}
                          onSet={(word, score) => handleSetCategoricalLabel(k, word, score)}
                          onDeleteWord={(word) => handleDeleteCategoricalLabel(k, word)}
                          computeWordStyle={
                            computeWordStyle ? (word) => computeWordStyle(k, word) : undefined
                          }
                          {...(wordProps ? wordProps(k, word) : undefined)}
                        />
                      )
                    })
                    .value()}
                </div>
              : null}
            </div>
          )
        })
        .value()}
    </div>
  )
}

export const labelingWidgetConfigCategoricals: ILabelingWidgetConfig<
  ICategoricalsLabel,
  ICategoricalLabelingWidgetProps
> = {
  control: LabelingWidget,
  computeLabelStyle,
  canDelete: true,
  deleteConfirmText: 'Delete ALL categorical labels?',
}

export {AddWords}
